/**
 * React component responsible for creating a basic section wrapper
 *
 * Copyright (c) 2020 Coursera
 * Author: Kelvin Nguyen
 */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames'
import ReactPlayer from 'react-player/lazy'
import { useMediaQuery } from 'react-responsive'
import Container from '@material-ui/core/Container'
import styles from './SectionWrapper.module.scss'

export const SectionWrapperDefaultProps = {
  backgroundColor: 'white',
  children: null,
}

export const SectionWrapperPropTypes = {
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  backgroundColor: PropTypes.oneOf(['blue', 'lightBlue', 'gray', 'purple', 'white']),
  backgroundImage: PropTypes.string,
  backgroundImageMobile: PropTypes.string,
  backgroundVideo: PropTypes.string,
  backgroundStyle: PropTypes.object,
  children: PropTypes.node,
}

const SectionWrapper = ({
  className,
  backgroundColor,
  backgroundImage,
  backgroundImageMobile,
  backgroundVideo,
  backgroundStyle,
  children,
}) => {
  const isLaptop = useMediaQuery({ maxWidth: styles.laptop })
  const isTablet = useMediaQuery({ maxWidth: styles.tablet })
  const sectionClass = cx(className, styles.section, {
    [styles[backgroundColor]]: !!backgroundColor,
    [styles.backgroundImage]: !!backgroundImage,
  })

  const backgroundImageStyle = useMemo(() => {
    const style = {
      backgroundPosition: 'center',
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      ...backgroundStyle,
    }

    if (backgroundImageMobile && isTablet) {
      style.backgroundImage = `url(${backgroundImageMobile})`
    } else if (backgroundImage) {
      style.backgroundImage = `url(${backgroundImage})`
    }

    return style
  }, [backgroundImage, backgroundImageMobile, backgroundStyle, isTablet])

  const BackgroundVideo = useMemo(() => (
    (backgroundVideo && !isTablet) ? (
      <ReactPlayer
        url={backgroundVideo}
        width={isLaptop ? 'auto' : '100%'}
        height="auto"
        playing
        loop
        muted />
    ) : null
  ), [backgroundVideo, isLaptop, isTablet])

  return (
    <section className={sectionClass} style={backgroundImageStyle}>
      <div className={cx(styles.videoPlayerWrapper)}>{BackgroundVideo}</div>
      <Container className={cx(styles.sectionContainer)}>
        {children}
      </Container>
    </section>
  )
}

SectionWrapper.defaultProps = SectionWrapperDefaultProps
SectionWrapper.propTypes = SectionWrapperPropTypes

export default SectionWrapper
